import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { fetchEmployeeCampaigns } from 'actions';
import { includes, isArray, isEmpty, map, find, isEqual } from 'lodash';
import { CustomDropdownContainer, CustomMultiOption } from 'CommonComponent';

const optionMatchesId = (opt, id) =>
  opt != null && String(opt.value) === String(id);

const dedupeOptionsByValue = (options = []) => {
  const seen = new Set();
  return options.filter((opt) => {
    if (!opt || opt.value == null) return false;
    const key = String(opt.value);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/** Stable defaults — `= []` in params creates a new array every render and retriggers effects. */
const EMPTY_IDS = Object.freeze([]);

const CampaignDropdown = ({
  officesIds = EMPTY_IDS,
  employeesIds = EMPTY_IDS,
  selectedId = '',
  selectedChangingId = '',
  campaignValue,
  setCampaignValue,
  campaignOnChange,
  setAllCampaignIds,
  showSelectAllOption = true,
  isMulti = true,
  placeholder = 'Select Campaign',
  label = 'Campaign',
  name = 'campaign_id',
  isDisabled = false,
  required = false,
}) => {
  const dispatch = useDispatch();
  const campaignList = useSelector(state => state.employeeState.campaignsList);
  const isCampaignsLoading = useSelector(state => state.employeeState.isCampaignsLoading);
  const [options, setOptions] = useState([]);
  const [offIds, setOffIds] = useState(officesIds);
  const [empIds, setEmpIds] = useState(employeesIds);

  useEffect(() => {
    setOffIds((prev) => (isEqual(prev, officesIds) ? prev : officesIds));
  }, [officesIds]);

  useEffect(() => {
    setEmpIds((prev) => (isEqual(prev, employeesIds) ? prev : employeesIds));
  }, [employeesIds]);

  // Fetch campaigns whenever office or employee IDs change
  useEffect(() => {
    let filteredOffices = includes(offIds, 'all') ? [] : offIds;
    let filteredEmployees = includes(empIds, 'all') ? [] : empIds;

    if ((!campaignList || campaignList.length === 0) && !isCampaignsLoading) {
      dispatch(fetchEmployeeCampaigns(filteredOffices, filteredEmployees));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offIds, empIds, campaignList, isCampaignsLoading]);

  // Hydrate from selectedChangingId (one-shot style updates)
  useEffect(() => {
    if (!selectedChangingId || !campaignList) return;

    if (isArray(selectedChangingId)) {
      const selected = dedupeOptionsByValue(
        selectedChangingId
          .map((id) => find(campaignList, (item) => item && String(item.id) === String(id)))
          .filter(Boolean)
          .map((item) => ({ value: item.id, label: item.name || item.client_name }))
      );
      setCampaignValue(selected);
    } else {
      const selected = find(campaignList, (item) => item && String(item.id) === String(selectedChangingId));
      setCampaignValue(selected ? { value: selected.id, label: selected.name || selected.client_name } : null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChangingId, campaignList]);

  // Build options from list (+ keep any selected values not in list)
  useEffect(() => {
    if (!campaignList) return;

    const formattedOptions = map(campaignList, (item) => ({
      value: item.id,
      label: item.name || item.client_name,
    }));

    if (showSelectAllOption && isMulti) {
      formattedOptions.unshift({ value: 'all', label: 'All' });
    }

    if (isMulti && Array.isArray(campaignValue)) {
      campaignValue.forEach((opt) => {
        if (opt && opt.value && opt.value !== 'all' && !find(formattedOptions, (o) => optionMatchesId(o, opt.value))) {
          formattedOptions.push(opt);
        }
      });
    }

    setOptions(dedupeOptionsByValue(formattedOptions));

    if (typeof setAllCampaignIds === 'function') {
      setAllCampaignIds(map(campaignList, 'id'));
    }
    // campaignValue intentionally omitted — including it remount-looped parent form state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignList, showSelectAllOption, isMulti]);

  // Hydrate controlled value from selectedId only when IDs/list change (not on every value edit).
  // Including campaignValue in deps previously restored stale selectedId and broke add/remove.
  useEffect(() => {
    if (isEmpty(selectedId)) return;
    if (!campaignList || campaignList.length === 0) return;

    const formattedOptions = map(campaignList, (item) => ({
      value: item.id,
      label: item.name || item.client_name,
    }));

    if (isArray(selectedId)) {
      const uniqueIds = [...new Set(selectedId.map((id) => String(id)))];
      const currentMulti = isArray(campaignValue) ? campaignValue : [];
      const selected = dedupeOptionsByValue(
        uniqueIds
          .map(
            (id) =>
              find(formattedOptions, (o) => optionMatchesId(o, id)) ||
              find(currentMulti, (o) => optionMatchesId(o, id))
          )
          .filter(Boolean)
      );

      if (!isEqual(campaignValue, selected)) {
        setCampaignValue(selected);
      }
    } else {
      const currentSingle = campaignValue && !isArray(campaignValue) ? campaignValue : null;
      const selected =
        find(formattedOptions, (o) => optionMatchesId(o, selectedId)) ||
        (currentSingle && optionMatchesId(currentSingle, selectedId) ? currentSingle : null);
      if (!isEqual(campaignValue, selected)) {
        setCampaignValue(selected || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignList, selectedId]);

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label className={`label-input${required ? " label-input--required" : ""}`}>{label}</Form.Label>
        <Select
          name={name}
          options={options}
          value={campaignValue}
          onChange={campaignOnChange}
          isMulti={isMulti}
          placeholder={placeholder}
          className="multi-select w-100"
          classNamePrefix="react-select"
          isSearchable
          isClearable
          isDisabled={isDisabled}
          hideSelectedOptions={false}
          closeMenuOnSelect={!isMulti}
          openMenuOnFocus
          components={{
            ValueContainer: CustomDropdownContainer,
            ...(isMulti ? { Option: CustomMultiOption } : {}),
          }}
        />
      </Form.Group>
    </>
  );
};

CampaignDropdown.propTypes = {
  campaignOnChange: PropTypes.func.isRequired,
  setCampaignValue: PropTypes.func.isRequired,
  campaignValue: PropTypes.any,
  officesIds: PropTypes.array,
  employeesIds: PropTypes.array,
  selectedId: PropTypes.any,
  selectedChangingId: PropTypes.any,
  setAllCampaignIds: PropTypes.func,
  isMulti: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
  isDisabled: PropTypes.bool,
  required: PropTypes.bool,
};

export default CampaignDropdown;
